// src/components/Contacts/ContactEmailHistory.js
import React, { useState, useEffect } from 'react';
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Paper,
    Chip,
    Box
} from '@mui/material';
import api from '../../api/axios';

const ContactEmailHistory = ({ contactId, campaignId }) => {
    const [messageHistory, setMessageHistory] = useState({ emails: [], messages: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHistory = async () => {
            try {
                if (campaignId) {
                    // Campaign modal: history scoped to one campaign
                    const response = await api.get(`/campaigns/${campaignId}/contact_history/`, {
                        params: { contact_id: contactId }
                    });
                    setMessageHistory(response.data);
                } else {
                    // Contacts page: all emails + round messages for this contact
                    const response = await api.get(`/campaign-emails/contact/${contactId}/`);
                    setMessageHistory(response.data);
                }
                setLoading(false);
            } catch (err) {
                console.error('Error fetching message history:', err);
                setError('Failed to load message history');
                setLoading(false);
            }
        };

        if (contactId) {
            fetchHistory();
        }
    }, [contactId, campaignId]);

    if (loading) return <CircularProgress />;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!messageHistory.messages?.length && !messageHistory.emails?.length) {
        return <Typography>No message history available</Typography>;
    }

    // Compact open-tracking summary for a sent item
    const renderOpenStats = (item) => {
        if (item.status !== 'sent' && !item.sent_at) return null;
        const opens = item.open_count || 0;
        return (
            <Typography
                component="span"
                variant="body2"
                display="block"
                color={opens ? 'success.main' : 'text.secondary'}
            >
                {opens
                    ? `Opened ${opens}× — first ${new Date(item.first_opened_at).toLocaleString()}`
                    : 'Not opened yet'}
            </Typography>
        );
    };

    return (
        <Paper sx={{ maxHeight: 400, overflow: 'auto', mt: 2 }}>
            <List dense>
                {/* Round-based messages */}
                {messageHistory.messages?.map((msg) => (
                    <ListItem key={`msg-${msg.id}`} divider>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                    {msg.subject || `${msg.channel_label} Message`}
                                    <Chip
                                        label={msg.status}
                                        size="small"
                                        color={msg.status === 'sent' ? 'success' : 'default'}
                                    />
                                </Box>
                            }
                            secondary={
                                <>
                                    <Typography component="span" variant="body2">
                                        {msg.round_name} - {msg.channel_label}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="body2">
                                        {msg.sent_at
                                            ? `Sent: ${new Date(msg.sent_at).toLocaleString()}`
                                            : `Generated: ${new Date(msg.generated_at).toLocaleString()}`
                                        }
                                    </Typography>
                                    {msg.channel === 'email' && renderOpenStats(msg)}
                                </>
                            }
                        />
                    </ListItem>
                ))}

                {/* Legacy campaign emails */}
                {messageHistory.emails?.map((email) => (
                    <ListItem key={`email-${email.id}`} divider sx={{ bgcolor: 'grey.50' }}>
                        <ListItemText
                            primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                    {email.subject}
                                    <Chip
                                        label={email.sent_at ? 'Sent' : 'Draft'}
                                        size="small"
                                        color={email.sent_at ? 'success' : 'default'}
                                    />
                                </Box>
                            }
                            secondary={
                                <>
                                    <Typography component="span" variant="body2">
                                        Campaign: {email.campaign_name}
                                    </Typography>
                                    <br />
                                    <Typography component="span" variant="body2">
                                        {email.sent_at
                                            ? `Sent: ${new Date(email.sent_at).toLocaleString()}`
                                            : `Created: ${new Date(email.created_at).toLocaleString()}`
                                        }
                                    </Typography>
                                    {renderOpenStats(email)}
                                </>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ContactEmailHistory;